/**
 * auth.js
 * Gestión de autenticación con Supabase.
 * Responsabilidad: login, logout, registro, aprobación
 * de usuarios y cambio de contraseña.
 */

import { supabase } from './supabase.js';

/**
 * Inicia sesión con email y contraseña.
 * @param {string} email
 * @param {string} password
 * @returns {{ user, error }}
 */
export async function login(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { user: data?.user ?? null, error };
}

/**
 * Cierra la sesión del usuario actual.
 */
export async function logout() {
  await supabase.auth.signOut();
}

/**
 * Devuelve el usuario actual o null si no hay sesión.
 * @returns {Promise<User|null>}
 */
export async function obtenerUsuario() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.user ?? null;
}

/**
 * Suscribe una función a los cambios de sesión.
 * Se llama automáticamente al hacer login o logout.
 * @param {Function} callback - recibe (event, session)
 */
export function onCambioSesion(callback) {
  supabase.auth.onAuthStateChange(callback);
}

/**
 * Comprueba si el usuario actual tiene rol admin.
 * @returns {Promise<boolean>}
 */
export async function esAdmin() {
  const usuario = await obtenerUsuario();
  if (!usuario) {
    return false;
  }

  const { data, error } = await supabase
    .from('perfiles')
    .select('rol')
    .eq('id', usuario.id)
    .single();

  if (error || !data) {
    return false;
  }
  return data.rol === 'admin';
}

/**
 * Registra un nuevo usuario con email y contraseña.
 * Supabase le manda un email de confirmación.
 * @param {string} email
 * @param {string} password
 * @returns {{ error }}
 */
export async function registro(email, password) {
  const { error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      emailRedirectTo: window.location.origin,
    },
  });

  // Notificar al admin si el registro fue exitoso
  if (!error) {
    try {
      await supabase.functions.invoke('notificar-registro', {
        body: { email },
      });
    } catch {
      // Si falla la notificación no bloqueamos el registro
      console.warn('[Auth] No se pudo enviar notificación de registro');
    }
  }

  return { error };
}

/**
 * Comprueba si el usuario actual está aprobado.
 * @returns {Promise<boolean>}
 */
export async function estaAprobado() {
  const usuario = await obtenerUsuario();
  if (!usuario) {
    return false;
  }

  try {
    const { data, error } = await supabase
      .from('perfiles')
      .select('aprobado')
      .eq('id', usuario.id)
      .single();

    if (error) {
      const cached = localStorage.getItem('aprobado');
      return cached === 'true';
    }

    // Guardar resultado para uso offline
    localStorage.setItem('aprobado', data.aprobado ? 'true' : 'false');
    return data.aprobado === true;
  } catch {
    // "Failed to fetch" — sin red, usar caché
    const cached = localStorage.getItem('aprobado');
    return cached === 'true';
  }
}

/**
 * Cambia la contraseña del usuario actual.
 * @param {string} nuevaPassword
 * @returns {{ error }}
 */
export async function cambiarPassword(nuevaPassword) {
  const { error } = await supabase.auth.updateUser({
    password: nuevaPassword,
  });
  return { error };
}
